import { Button, Checkbox, Form, FormProps, Input, Radio, Select } from "antd";
import { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from "../../../assets/icons/AddIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import PdfIcon from "../../../assets/icons/PdfIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import AdminLayout from "../../../layouts/AdminLayout";
import { formats } from "../../../lib/TextEditor/Formats";
import { modules } from "../../../lib/TextEditor/Modules";
import { getAllCategoryAsync } from "../../../store/features/category/categoryApi";
import {
  createPublicationAsync,
  singPublicationAsync,
  updatePublicationAsync,
} from "../../../store/features/contentManagment/contentManagmentApi";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import {
  CreateContentData,
  GetSingleContentData,
} from "../../../types/contentManagment/contentManagment";
import { ArrayOption, selectSearchOption } from "../../../utils/utils";
import CustomSection from "../../ui/CustomSection";
import { DimensionOfImage } from "../../ui/DimensionOfImage";
import FileUpload from "../../ui/FileUpload";
import IconButton from "../../ui/IconButton";
import IconicTitle from "../../ui/IconicTitle";

export default function AddPublication() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [code, setCode] = useState("Start writing from here...");
  const [checkAll, setCheckAll] = useState(false);
  const { manageLoading, singleContentData } = useAppSelector(
    (state) => state.publication
  );
  const { CategoryList } = useAppSelector((state) => state.category);

  const createContentData =
    singleContentData as unknown as GetSingleContentData;

  const { login } = useAppSelector((state) => state.login);

  const statusList = [
    "Published",
    "CSO-Admin-Approved",
    "Draft",
    "Archived",
    "Waiting_For_Approval",
    "Back_To_Author",
  ];

  const filteredStatusList =
    login?.role_id?.role_type === "cso_admin"
      ? statusList.filter((item) => item !== "Published")
      : statusList.filter((item) => item !== "CSO-Admin-Approved");

  const onFinish: FormProps<any>["onFinish"] = (values) => {
    const { ...rest } = values;
    const newValue = {
      ...rest,
    };

    if (id) {
      dispatch(
        updatePublicationAsync({
          data: newValue,
          others: {
            reRender,
            id: createContentData?._id,
          },
        })
      );
    } else {
      dispatch(
        createPublicationAsync({
          data: newValue,
          others: {
            reRender,
          },
        })
      );
    }
  };

  const handleProcedureContentChange = (value: string) => {
    setCode(value);
    form.setFieldsValue({ publicationContent: value });
  };

  // dispatch content
  useEffect(() => {
    dispatch(
      getAllCategoryAsync({
        params: {
          skip: 0,
          limit: 0,
          custom_text: "",
          categoryType: "Publication",
          categoryStatus: "Published",
        },
      })
    );
  }, [dispatch]);

  //single publication
  useEffect(() => {
    if (id) {
      dispatch(
        singPublicationAsync({
          params: {
            id,
          },
        })
      );
    }
  }, [dispatch, id]);

  // add value in modal for view and edit
  useEffect(() => {
    if (id && createContentData?._id && manageLoading === false) {
      form.setFieldsValue({
        publicationTitle: createContentData?.publicationTitle,
        categoryId: createContentData?.categoryId?._id,
        publicationThumbnail: createContentData?.publicationThumbnail,
        publicationFiles: createContentData?.publicationFiles?.map(
          (val) => val
        ),
        publicationContent: createContentData?.publicationContent,
        publicationStatus: createContentData?.publicationStatus,
      });
      setCode(createContentData?.publicationContent || "");
    }
  }, [
    createContentData?._id,
    createContentData?.categoryId,
    createContentData?.publicationContent,
    createContentData?.publicationFiles,
    createContentData?.publicationStatus,
    createContentData?.publicationThumbnail,
    createContentData?.publicationTitle,
    form,
    id,
    manageLoading,
  ]);

  function reRender(val: boolean) {
    if (val) {
      navigate("/admin/content-manage/content");
    }
  }

  const handleCheckAll = (e: any) => {
    const checked = e.target.checked;
    setCheckAll(checked);
  };

  return (
    <AdminLayout
      title={`${id ? "Content Update" : "Create a New Content"}`}
      backLink="/admin/content-manage/content"
    >
      <Form
        className="custom-form space-y-[14px]"
        layout="vertical"
        onFinish={onFinish}
        size="large"
        form={form}
      >
        <CustomSection>
          <div className="grid grid-cols-2 gap-x-2.5 w-full">
            <Form.Item
              name="categoryId"
              label="Select Category"
              rules={[
                {
                  required: true,
                  message: "Please enter address",
                },
              ]}
            >
              <Select
                placeholder="Select"
                size="large"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={ArrayOption(CategoryList, "categoryName", "_id")}
                suffixIcon={<SelectDownIcon />}
              />
            </Form.Item>

            <Form.Item
              name="publicationThumbnail"
              label="Chose a Thumbnail"
              extra={DimensionOfImage({ width: 1032, height: 435 })}
              valuePropName="fileUrl"
              rules={[
                {
                  required: true,
                  message: "Please upload photo",
                },
              ]}
            >
              <FileUpload
                form={form}
                accept={acceptFile}
                name="image"
                onChange={(url) =>
                  form.setFieldsValue({ image: url || undefined })
                }
              />
            </Form.Item>

            <Form.Item
              className="col-span-2"
              name="publicationTitle"
              label="Post Title"
              rules={[
                {
                  required: true,
                  message: "Please enter title",
                },
              ]}
            >
              <Input placeholder="Enter title here" size="large" />
            </Form.Item>

            <Form.Item
              className="col-span-2"
              name="publicationContent"
              rules={[
                {
                  required: true,
                  message: "Please enter title",
                },
              ]}
            >
              <ReactQuill
                className="h-[372px]"
                theme="snow"
                formats={formats}
                modules={modules}
                value={code}
                onChange={handleProcedureContentChange}
              />
              <br />
              <br />
            </Form.Item>
          </div>
        </CustomSection>

        <CustomSection>
          <IconicTitle
            icon={<PdfIcon />}
            className="text-primary"
            title="PDF File Attachment"
          />

          <div className="w-full mt-4">
            <p className="text-body font-semibold">Guest Photo</p>
            <Form.List initialValue={[undefined]} name="publicationFiles">
              {(fields, { add, remove }) => {
                return (
                  <>
                    {fields.map((field, index) => (
                      <div className="relative" key={field.key}>
                        <br />
                        <Form.Item
                          name={[index]}
                          valuePropName="fileUrl"
                          rules={[
                            {
                              required: true,
                              message: "Please upload photo",
                            },
                          ]}
                        >
                          <FileUpload
                            form={form}
                            accept={acceptFile}
                            name="image"
                            onChange={(url) =>
                              form.setFieldsValue({ image: url || undefined })
                            }
                          />
                        </Form.Item>

                        {fields.length > 1 ? (
                          <div className="flex justify-end mt-1 absolute right-0 -top-0">
                            <button
                              className="rounded-full bg-transparent border-none cursor-pointer"
                              onClick={() => remove(field.name)}
                            >
                              <span className="relative top-[1px]">
                                <DeleteIcon />
                              </span>
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ))}

                    <div
                      className={`flex items-end justify-end mb-3 ${
                        fields?.length % 2 === 0 ? "mt-6" : "mt-2"
                      }`}
                    >
                      <IconButton
                        className="!text-sm !font-normal !rounded-lg !text-primary !bg-[#FFF5F4]"
                        type="text"
                        icon={<AddIcon color="#dc3545 " />}
                        onClick={() => add()}
                      >
                        <span>Add More</span>
                      </IconButton>
                    </div>
                  </>
                );
              }}
            </Form.List>
          </div>
        </CustomSection>

        <CustomSection>
          <div className="flex items-center gap-x-2.5 mb-4">
            <Form.Item
              name="publicationStatus"
              rules={[
                {
                  required: true,
                  message: "Please enter title",
                },
              ]}
            >
              <Radio.Group className="custom-radio" size="large">
                {filteredStatusList?.map((el, idx) => (
                  <Radio
                    className="text-body text-base font-medium leading-7 mr-3.5"
                    value={el}
                    key={idx}
                  >
                    {el}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </div>

          <div className="border border-solid border-[#d7dee8] mt-1 mb-4 w-full" />

          <div className="flex justify-between items-center">
            <Checkbox checked={checkAll} onChange={handleCheckAll} className="">
              <span className="text-body text-sm font-medium leading-normal">
                Yes! I confirmed and Publish this event
              </span>
            </Checkbox>
            <div className="flex gap-x-[12px]">
              <Button className="cancel-btn" type="text" size="large">
                Cancel
              </Button>

              <Button
                className="primary_btn"
                type="primary"
                size="large"
                htmlType="submit"
              >
                Save
              </Button>
            </div>
          </div>
        </CustomSection>
      </Form>
    </AdminLayout>
  );
}

const acceptFile = ".jpg, .jpeg, .png";
